import type { InteractionMessageResponse } from '@vagusx/clients';
import type { TimestampMode } from './timecode';

/**
 * Connection state for the SSE stream — mirrors the hook's state.
 */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Built-in header actions are opt-in via presence of their callback.
 * `additionalActions` is the escape hatch for future surfaces.
 */
export interface ConversationViewHeader {
  /** Shows "● Live · 00:42" on the left when present. */
  liveStatus?: { isLive: boolean; durationMs?: number };
  /** Tool-calls toggle. Not rendered if omitted. */
  showToolCalls?: { value: boolean; onChange: (value: boolean) => void };
  /** Download-transcript button. Not rendered if omitted. */
  onDownload?: () => void;
  /** Extra nodes after built-ins (right-aligned). */
  additionalActions?: React.ReactNode;
}

/**
 * Audio companion for seek-on-click + playback. Enables the sticky
 * audio bar at the bottom of the widget when a source is provided.
 *
 * Two shapes, distinguished by presence of dual-track fields:
 *
 *   - **Mixed:** `{ src }` — single stereo recording (legacy).
 *   - **Dual-track (R-2):** `{ callerSrc, agentSrc, callerOffsetMs?, agentOffsetMs? }`
 *     — separate tracks played in lockstep. The bar picks the track
 *     with the smaller offset as leader; the follower's `currentTime`
 *     is kept in sync across seeks + natural playback.
 *
 * Consumers pick one shape. `isDualTrack` is the runtime guard.
 */
export interface ConversationAudioProps {
  /**
   * ISO timestamp of the recording's FIRST SAMPLE — the anchor every
   * transcript offset is measured from
   * (`interactions.recording_started_at_ms`).
   *
   * OPTIONAL, and omitting it is the correct answer for a call whose anchor
   * was never recorded. Everything alignment-dependent then switches off
   * together: no line is highlighted and no line is clickable to seek. Do
   * NOT substitute a nearby timestamp to fill the hole — `started_at` is
   * seconds early by a per-call margin, and a sentinel like the epoch turns
   * every click into a seek past the end of the file. Playback and the
   * scrubber work regardless.
   */
  startedAt?: string;
  /**
   * ISO instant the FAR END picked up (`interactions.sip_answered_at`).
   *
   * Its distance from `startedAt` is the recording's silent pre-pickup head —
   * egress begins while the line is still ringing, and no carrier early media
   * reaches the room, so that stretch records as the ambience floor. Marking
   * it is the whole reason this prop exists (ADR-339); see `ringLeadIn`.
   *
   * OPTIONAL, and absent is the common case: inbound calls and browser tests
   * never observe a SIP answer, nor does a call nobody picked up, nor any row
   * predating the column. Both the transcript's ring row and the scrubber's
   * shade then switch off together. NEVER substitute `answeredAt` from the
   * interaction — that is stamped when the runtime POSTs `/start`, which on an
   * outbound dial is mid-ring, and a shade in the wrong place is worse than no
   * shade.
   */
  answeredAt?: string;
  /** Current playback position in ms, relative to `startedAt`. Controlled. */
  currentMs?: number;
  /** Fired on message click OR scrubber drag. */
  onSeek?: (relativeMs: number) => void;
  /** Fired on play / pause. */
  onPlayingChange?: (playing: boolean) => void;
  /** Whether playback is currently on. Controlled. */
  isPlaying?: boolean;

  /** Legacy mixed-recording URL. Mutually exclusive with caller/agent srcs. */
  src?: string;

  /**
   * Total length in ms, if the server already knows it
   * (`interactions.recording_duration_seconds`, written by the egress
   * webhook).
   *
   * Renders the duration and arms the scrubber on first paint, before any
   * bytes are fetched. Without it the bar has to wait for `loadedmetadata`
   * and shows "0:00 / 0:00" with a dead scrubber until then — the same
   * defect the native player had with `preload="none"`. The measured value
   * replaces it once metadata lands.
   */
  durationMs?: number;

  /** Dual-track caller URL. If set, `agentSrc` must also be set. */
  callerSrc?: string;
  /** Dual-track agent URL. If set, `callerSrc` must also be set. */
  agentSrc?: string;
  /** Offset of caller's first sample vs `startedAt`, in ms. Default 0. */
  callerOffsetMs?: number;
  /** Offset of agent's first sample vs `startedAt`, in ms. Default 0. */
  agentOffsetMs?: number;
}

/** Narrowed shape once `isDualTrack` passes. */
export interface ConversationAudioDualTrack extends ConversationAudioProps {
  callerSrc: string;
  agentSrc: string;
}

export function isDualTrack(props: ConversationAudioProps): props is ConversationAudioDualTrack {
  return typeof props.callerSrc === 'string' && typeof props.agentSrc === 'string';
}

export interface ConversationViewProps {
  /** Transcript messages, sorted ascending by sequence. */
  messages: InteractionMessageResponse[];
  /** Whether to render tool_request / tool_response rows. Default false. */
  showToolCalls?: boolean;
  /** Live indicator + auto-scroll-to-newest behavior. */
  isLive?: boolean;
  /** Connection state banner. */
  connectionState?: ConnectionState;
  /** Header configuration. Pass `false` to hide the header. */
  header?: ConversationViewHeader | false;
  /** Optional audio companion. */
  audio?: ConversationAudioProps;
  /**
   * Whether to render the sticky transport at the foot of the widget.
   * Default true.
   *
   * Set false when the consumer places `AudioBar` itself. The call-detail
   * sheet does: its body owns an outer scroll and the transcript is floored
   * at `min-h-64`, so a bar at the foot of the transcript sits below the fold
   * on open — the reader sees a transcript and no player. `audio` still
   * drives the highlight and click-to-seek either way; this only moves where
   * the controls are drawn.
   */
  showAudioBar?: boolean;
  /**
   * Where the transcript puts its timestamps. Default `'hover'` — the
   * long-standing behaviour, and the right one for a live call where the
   * reader is watching text arrive rather than navigating a recording.
   *
   * `'always'` adds a fixed gutter column carrying every row's position in the
   * recording, which turns the transcript into a time axis. The call-detail
   * sheet passes it; see {@link TimestampMode}.
   */
  showTimestamps?: TimestampMode;
  /** Override the "no messages yet" state. */
  emptyState?: React.ReactNode;
  className?: string;
}

export type PairedEntry =
  | { kind: 'user_message'; key: string; message: InteractionMessageResponse }
  | { kind: 'agent_message'; key: string; message: InteractionMessageResponse }
  | { kind: 'tool_request'; key: string; request: InteractionMessageResponse }
  | { kind: 'handoff'; key: string; event: InteractionMessageResponse }
  | { kind: 'call_ended'; key: string; event: InteractionMessageResponse };

export interface PairedMessages {
  /** Messages in render order; tool_response rows are absorbed into their paired request. */
  entries: PairedEntry[];
  /** Map of toolCallId → response message (for chip rendering). */
  responsesByCallId: Map<string, InteractionMessageResponse>;
}

/**
 * Build render-order entries + response lookup from a raw message
 * stream. Pure (no React) — exported for unit tests and any non-widget
 * consumers.
 */
/**
 * The order the words were said in: by `timestamp`, then by `sequence`.
 *
 * `sequence` is insertion order, and a patient line the reach recogniser
 * recorded late (it waits out the realtime leg's lag before the transcript
 * takes its text) inserts AFTER the agent's reply to it while carrying the
 * earlier timestamp — the list showed the answer before the question. One
 * comparator for the list and the audio sync, so a bubble and the playhead
 * can never disagree about a tie; timestamps parsed once, not per compare.
 */
export function inSpokenOrder(
  messages: InteractionMessageResponse[]
): InteractionMessageResponse[] {
  return messages
    .map((m) => ({ m, t: Date.parse(m.timestamp) }))
    .sort((a, b) => a.t - b.t || a.m.sequence - b.m.sequence)
    .map(({ m }) => m);
}

export function pairToolCalls(input: InteractionMessageResponse[]): PairedMessages {
  const messages = inSpokenOrder(input);
  const responsesByCallId = new Map<string, InteractionMessageResponse>();
  for (const m of messages) {
    if (m.kind === 'tool_response' && m.toolCallId) {
      responsesByCallId.set(m.toolCallId, m);
    }
  }
  const entries: PairedEntry[] = [];
  // call_ended markers are collected separately and appended AFTER every other
  // entry. The marker is written on the teardown path and its server sequence
  // can race an in-flight fire-and-forget transcript write (e.g. the final
  // goodbye line), so relying on sequence order could render a stray message
  // below the "call ended" row. Sorting it to the end makes "last row" a
  // rendering guarantee, not a timing gamble. See run.ts / ADR-116.
  const terminalEntries: PairedEntry[] = [];
  for (const m of messages) {
    if (m.kind === 'tool_response') continue;
    if (m.kind === 'tool_request') {
      entries.push({ kind: 'tool_request', key: m.id, request: m });
    } else if (m.kind === 'user_message') {
      entries.push({ kind: 'user_message', key: m.id, message: m });
    } else if (m.kind === 'agent_message') {
      entries.push({ kind: 'agent_message', key: m.id, message: m });
    } else if (m.kind === 'handoff') {
      entries.push({ kind: 'handoff', key: m.id, event: m });
    } else if (m.kind === 'call_ended') {
      terminalEntries.push({ kind: 'call_ended', key: m.id, event: m });
    }
  }
  return { entries: [...entries, ...terminalEntries], responsesByCallId };
}
